import React, { useState, useEffect } from 'react';
import { useProAuth } from '@/lib/ProAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, CreditCard, History, ArrowRight,
  Loader2, AlertTriangle, CheckCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SMS Credit Package Card
 */
const PackageCard = ({ pkg, onSelect, loading }) => {
  const isFeatured = pkg.is_featured;
  
  return (
    <Card 
      className={`relative cursor-pointer transition-all hover:shadow-lg ${
        isFeatured ? 'border-2 border-orange-400 ring-2 ring-orange-100' : 'hover:border-orange-200'
      }`}
      onClick={() => onSelect(pkg)}
    >
      {isFeatured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white">
            <Sparkles className="h-3 w-3 mr-1" />
            Best Value
          </Badge>
        </div>
      )}
      <CardContent className="pt-6">
        <div className="text-center space-y-3">
          <div className="text-3xl font-bold text-gray-900">
            {pkg.credits}
            <span className="text-sm font-normal text-gray-500 ml-1">SMS</span>
          </div>
          <div className="text-2xl font-semibold text-orange-600">
            €{pkg.price_eur.toFixed(2)}
          </div>
          <div className="text-xs text-gray-500">
            €{pkg.price_per_credit.toFixed(3)} per SMS
          </div>
          {pkg.savings_percent && (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              Save {pkg.savings_percent}%
            </Badge>
          )}
          <Button 
            className="w-full mt-4" 
            variant={isFeatured ? "default" : "outline"}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Select'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * SMS Wallet Component
 * Shows balance, allows purchasing credits, displays transaction history
 */
const SmsWalletCard = () => {
  const { isAuthenticated } = useProAuth();
  const [wallet, setWallet] = useState(null);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [showPackages, setShowPackages] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWalletData();
    }
  }, [isAuthenticated]);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      // Fetch balance
      const balanceRes = await fetch(`${API_URL}/api/sms-wallet/balance`, {
        credentials: 'include'
      });
      
      if (balanceRes.ok) {
        const balanceData = await balanceRes.json();
        setWallet(balanceData);
      }

      // Fetch packages (public)
      const packagesRes = await fetch(`${API_URL}/api/sms-wallet/packages`);
      if (packagesRes.ok) {
        const packagesData = await packagesRes.json();
        setPackages(packagesData.packages || []);
      }

      // Fetch recent transactions
      const txRes = await fetch(`${API_URL}/api/sms-wallet/transactions?limit=5`, {
        credentials: 'include'
      });
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg) => {
    setPurchaseLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/sms-wallet/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: pkg.package_id,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      toast.error(error.message);
      setPurchaseLoading(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="sms-wallet-card">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
        </CardContent>
      </Card>
    );
  }

  if (!wallet) {
    return null;
  }

  const isLowBalance = wallet.low_balance;

  return (
    <Card data-testid="sms-wallet-card" className={isLowBalance ? 'border-amber-300' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            SMS Wallet
          </div>
          {isLowBalance && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Low Balance
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          SMS credits for instant provider notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Balance Display */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{wallet.total_available}</div>
            <div className="text-xs text-gray-500">Available</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{wallet.quota_remaining}</div>
            <div className="text-xs text-gray-500">Monthly Quota</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{wallet.wallet_balance}</div>
            <div className="text-xs text-gray-500">Purchased</div>
          </div>
        </div>

        {/* Total SMS Sent */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">Total SMS sent</span>
          <span className="font-medium">{wallet.total_sms_sent}</span>
        </div>

        {/* Buy Credits Button */}
        <Button 
          className="w-full" 
          onClick={() => setShowPackages(!showPackages)}
          variant={isLowBalance ? 'default' : 'outline'}
        >
          <CreditCard className="h-4 w-4 mr-2" />
          {showPackages ? 'Hide Packages' : 'Buy SMS Credits'}
        </Button>

        {/* Credit Packages */}
        {showPackages && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {packages.map((pkg) => (
                <PackageCard 
                  key={pkg.package_id}
                  pkg={pkg}
                  onSelect={handlePurchase}
                  loading={purchaseLoading}
                />
              ))}
            </div>
          </div>
        )}

        {/* Transaction History Toggle */}
        <Button 
          variant="ghost" 
          className="w-full text-gray-600"
          onClick={() => setShowHistory(!showHistory)}
        >
          <History className="h-4 w-4 mr-2" />
          {showHistory ? 'Hide History' : 'View History'}
          <ArrowRight className={`h-4 w-4 ml-auto transition-transform ${showHistory ? 'rotate-90' : ''}`} />
        </Button>

        {/* Transaction History */}
        {showHistory && transactions.length > 0 && (
          <div className="space-y-2 border-t pt-4">
            {transactions.map((tx, idx) => (
              <div 
                key={tx.transaction_id || idx}
                className="flex items-center justify-between py-2 text-sm"
              >
                <div className="flex items-center gap-2">
                  {tx.amount > 0 ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <MessageSquare className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-gray-600">{tx.description}</span>
                </div>
                <span className={tx.amount > 0 ? 'text-green-600 font-medium' : 'text-gray-500'}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmsWalletCard;
